import { useCallback, useEffect, useState, useContext } from "react";
import { FilterContext } from "../FilterContextProvider";
import useAllProducts from "./useAllProducts";
import useApi from "../Api/Api";

const useSalesSummary = (str) => {
  const { filterData } = useContext(FilterContext);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const {
    allProducts,
    allProdsCategoryFilter,
    setAllProdsCategoryFilter,
    allProdsSearchFilter,
    setAllProdsSearchFilter,
    setAllProducts,
  } = useAllProducts(str);
  const api = useApi();
  const [salesData, setSalesData] = useState({
    showSales: [],
    allTimeShowSales: [],
    nonShowSales: [],
    allTimeNonShowSales: [],
    adjustableEndDateShowSales: [],
    adjustableEndDateNonShowSales: [],
  });
  const [eventIds, setEventIds] = useState([]);
  const productsToRender = [...allProducts];

  const noMatch = {
    purchases: 0,
    count_in: 0,
    count_out: 0,
    add: 0,
    add_2: 0,
    add_3: 0,
    add_4: 0,
    discount_quantity: 0,
    damage_quantity: 0,
    mail_order: 0,
    ctm_comp: 0,
    management_comp: 0,
    band_comp_quantity: 0,
    inventory_adjust: 0,
    event_count: 0,
    gross_cost: 0,
    gross_sales: 0,
    discount_sales: 0,
    discount_cost: 0,
    per_show: 0,
    ctm_comp_cost: 0,
    management_comp_cost: 0,
    band_comp_quantity_cost: 0,
    mail_order_cost: 0,
    damage_quantity_cost: 0,
    unavailable: 0,
    current: 0,
    sold: 0,
  };

  productsToRender.forEach((prod) => {
    if (prod.size_other) {
      prod.product_size = prod.size_other;
    } else if (prod?.size?.name) {
      prod.product_size = prod.size.name;
    } else if (!prod.size) {
      prod.product_size = null;
    }
    const showSale = salesData.showSales.find(({ product_id }) => product_id === prod.id) || noMatch;
    const allTimeShowSale = salesData.allTimeShowSales.find(({ product_id }) => product_id === prod.id) || noMatch;
    const nonShowSale = salesData.nonShowSales.find(({ product_id }) => product_id === prod.id) || noMatch;
    const allTimeNonShowSale = salesData.allTimeNonShowSales.find(({ product_id }) => product_id === prod.id) || noMatch;
    const adjustableEndDateShowSale =
      salesData.adjustableEndDateShowSales.find(({ product_id }) => product_id === prod.id) || noMatch;
    const adjustableEndDateNonShowSale =
      salesData.adjustableEndDateNonShowSales.find(({ product_id }) => product_id === prod.id) || noMatch;
    prod.ctm_comp = showSale?.ctm_comp + nonShowSale?.ctm_comp;
    prod.management_comp = showSale?.management_comp + nonShowSale?.management_comp;
    prod.band_comp_quantity = showSale?.band_comp_quantity + nonShowSale?.band_comp_quantity;
    prod.damage_quantity = showSale?.damage_quantity + nonShowSale?.damage_quantity;
    prod.discount_quantity = showSale?.discount_quantity + nonShowSale?.discount_quantity;
    prod.gross_cost = showSale?.gross_cost;
    prod.discount_cost = showSale?.discount_cost;
    prod.gross_sales = showSale?.gross_sales;
    prod.discount_sales = showSale?.discount_sales;
    prod.ctm_comp_cost = nonShowSale?.ctm_comp_cost + showSale?.ctm_comp_cost;
    prod.management_comp_cost = nonShowSale?.management_comp_cost + showSale?.management_comp_cost;
    prod.band_comp_quantity_cost = nonShowSale?.band_comp_quantity_cost + showSale?.band_comp_quantity_cost;
    prod.damage_quantity_cost = nonShowSale?.damage_quantity_cost + showSale?.damage_quantity_cost;
    prod.mail_order = nonShowSale?.mail_order;
    prod.mail_order_cost = nonShowSale?.mail_order_cost;
    prod.unavailable = nonShowSale?.unavailable;
    prod.purchases = nonShowSale?.purchases;
    prod.inventory_adjust = nonShowSale?.inventory_adjust;
    prod.sold = showSale?.sold;
    prod.per_show = (showSale?.sold + showSale?.discount_quantity) / showSale?.event_count;
    prod.event_count = showSale?.event_count;
    const current =
      allTimeNonShowSale?.purchases +
      allTimeShowSale?.count_out -
      allTimeShowSale?.count_in -
      allTimeShowSale?.add -
      allTimeShowSale?.add_2 -
      allTimeShowSale?.add_3 -
      allTimeShowSale?.add_4 -
      allTimeNonShowSale?.damage_quantity -
      allTimeNonShowSale?.discount_quantity -
      allTimeNonShowSale?.band_comp_quantity -
      allTimeNonShowSale?.ctm_comp -
      allTimeNonShowSale?.management_comp -
      allTimeNonShowSale?.mail_order -
      allTimeNonShowSale?.inventory_adjust;
    const adjustableEndDateCurrent =
      adjustableEndDateNonShowSale?.purchases +
      adjustableEndDateShowSale?.count_out -
      adjustableEndDateShowSale?.count_in -
      adjustableEndDateShowSale?.add -
      adjustableEndDateShowSale?.add_2 -
      adjustableEndDateShowSale?.add_3 -
      adjustableEndDateShowSale?.add_4 -
      adjustableEndDateNonShowSale?.damage_quantity -
      adjustableEndDateNonShowSale?.discount_quantity -
      adjustableEndDateNonShowSale?.band_comp_quantity -
      adjustableEndDateNonShowSale?.ctm_comp -
      adjustableEndDateNonShowSale?.management_comp -
      adjustableEndDateNonShowSale?.mail_order -
      adjustableEndDateNonShowSale?.inventory_adjust;
    prod.current = current;
    prod.adjustableEndDateCurrent = adjustableEndDateCurrent;
    prod.currently_available = current - allTimeNonShowSale?.unavailable;
    prod.value = current * prod.cost;
    if (
      salesData.showSales.find(({ product_id }) => product_id === prod.id) ||
      salesData.nonShowSales.find(({ product_id }) => product_id === prod.id)
    ) {
      prod.has_history = true;
    }
  });

  const grossSales = productsToRender.reduce((a, b) => {
    return a + b.gross_sales;
  }, 0);

  const totalCOGS = productsToRender.reduce((a, b) => {
    return a + b.gross_cost;
  }, 0);

  // Fetch all 6 sales datasets in parallel, update state once when all complete
  useEffect(() => {
    setResultsLoading(true);
    setLoadError(false);

    const dateRangeParams = {};
    if (filterData.start_date) dateRangeParams.start_date = filterData.start_date;
    if (filterData.end_date) dateRangeParams.end_date = filterData.end_date;
    if (filterData.artist_id) dateRangeParams.artist_id = filterData.artist_id;

    const artistOnlyParams = {};
    if (filterData.artist_id) artistOnlyParams.artist_id = filterData.artist_id;

    const endDateParams = {};
    if (filterData.end_date) endDateParams.end_date = filterData.end_date;
    if (filterData.artist_id) endDateParams.artist_id = filterData.artist_id;

    const dateRangeWithEventsQs = [
      ...Object.keys(dateRangeParams).map((p) => `${p}=${dateRangeParams[p]}`),
      ...Object.keys(eventIds).map((id) => `event_ids[]=${id}`),
    ].join("&");

    const artistOnlyQs = Object.keys(artistOnlyParams)
      .map((p) => `${p}=${artistOnlyParams[p]}`)
      .join("&");

    const endDateQs = Object.keys(endDateParams)
      .map((p) => `${p}=${endDateParams[p]}`)
      .join("&");

    Promise.all([
      api.get(`/sales/summary?${dateRangeWithEventsQs}`),
      api.get(`/sales/summary?${artistOnlyQs}`),
      api.get(`/sales/notshow?${dateRangeWithEventsQs}`),
      api.get(`/sales/notshow?${artistOnlyQs}`),
      api.get(`/sales/summary?${endDateQs}`),
      api.get(`/sales/notshow?${endDateQs}`),
    ]).then(([
      showSalesRes,
      allTimeShowSalesRes,
      nonShowSalesRes,
      allTimeNonShowSalesRes,
      adjustableEndDateShowSalesRes,
      adjustableEndDateNonShowSalesRes,
    ]) => {
      setSalesData({
        showSales: showSalesRes.data,
        allTimeShowSales: allTimeShowSalesRes.data,
        nonShowSales: nonShowSalesRes.data,
        allTimeNonShowSales: allTimeNonShowSalesRes.data,
        adjustableEndDateShowSales: adjustableEndDateShowSalesRes.data,
        adjustableEndDateNonShowSales: adjustableEndDateNonShowSalesRes.data,
      });
      setResultsLoading(false);
    }).catch(() => {
      setResultsLoading(false);
      setLoadError(true);
    });
  }, [api, filterData, eventIds]);

  // Standalone refresh for external callers (LocationInventory, AdjustInventory)
  const getAllTimeNonShowSales = useCallback(() => {
    let queryParams = {};
    if (filterData.artist_id) {
      queryParams.artist_id = filterData.artist_id;
    }
    const queryString = Object.keys(queryParams)
      .map((param) => `${param}=${queryParams[param]}`)
      .join("&");
    api.get(`/sales/notshow?${queryString}`).then((r) => {
      setSalesData((prev) => ({ ...prev, allTimeNonShowSales: r.data }));
    });
  }, [api, filterData]);

  const resetPagination = () => {
    setAllProducts([]);
  };

  return {
    productsToRender,
    grossSales,
    totalCOGS,
    allProdsCategoryFilter,
    setAllProdsCategoryFilter,
    allProdsSearchFilter,
    setAllProdsSearchFilter,
    getAllTimeNonShowSales,
    resultsLoading,
    loadError,
    resetPagination,
    eventIds,
    setEventIds,
  };
};

export default useSalesSummary;
